<template>
  <div
    v-if="clientDocumentations.length > 0"
    class="doc-person"
    :class="{ 'doc-person--compact': compact }"
  >
    <button
      type="button"
      class="doc-person__header"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <div class="doc-person__identity min-w-0">
        <ShowUserImage
          v-if="imgUrl"
          :url="imgUrl"
          :width="'32'"
          :height="'32'"
          class="doc-person__avatar"
        />
        <span
          v-else
          class="doc-person__avatar doc-person__avatar--fallback"
          :style="{ background: avatarColor }"
          aria-hidden="true"
        >
          {{ initials }}
        </span>
        <span v-if="relationLabel" class="doc-person__relation">
          {{ relationLabel }}
        </span>
        <span class="doc-person__name">{{ name }}</span>
      </div>
      <div class="doc-person__meta">
        <span
          class="doc-person__badge"
          :class="
            incompleteCount
              ? 'doc-person__badge--pending'
              : 'doc-person__badge--ok'
          "
        >
          {{
            incompleteCount
              ? `${incompleteCount} pendente${incompleteCount === 1 ? "" : "s"}`
              : "Tudo concluído"
          }}
        </span>
        <i
          class="pi doc-person__chevron"
          :class="expanded ? 'pi-chevron-up' : 'pi-chevron-down'"
          aria-hidden="true"
        />
      </div>
    </button>

    <ProgressBar
      v-if="isLoading"
      mode="indeterminate"
      class="doc-person__progress"
    />

    <div v-show="expanded && !isLoading" class="doc-person__body">
      <DocumentationDocRow
        v-for="(documentation, index) in clientDocumentations"
        :key="
          documentation.id ||
          (documentation.documentationType &&
            documentation.documentationType.id) ||
          index
        "
        :documentation="documentation"
        @open="openForm(documentation)"
      />
    </div>

    <Sidebar
      v-model:visible="sidebarVisible"
      position="right"
      :title="selectedDocumentation ? selectedDocumentation.name : ''"
      class="doc-person__sidebar"
      :style="{ width: '680px', maxWidth: '100%' }"
      @hide="closeForm"
    >
      <div v-if="selectedDocumentation" class="doc-person__sidebar-content">
        <ClientDocumentationForm
          :documentation-type="selectedDocumentation"
          :index="selectedDocumentation.uiId"
          :uploader="formUploaderConfig"
          :ui="formUiConfig"
          class="doc-person__form"
          @upload="handleFormUpload"
        />

        <div class="doc-person__sidebar-actions">
          <Button
            :disabled="isSubmitting"
            :label="$t('documentation_types_submit')"
            icon="pi pi-check"
            class="p-button-sm"
            @click="submitSelected"
          />
          <Button
            :disabled="isSubmitting"
            :label="$t('cancel')"
            class="p-button-sm p-button-secondary"
            @click="sidebarVisible = false"
          />
        </div>
      </div>
    </Sidebar>
  </div>
</template>

<script>
import ProgressBar from "primevue/progressbar";
import Sidebar from "primevue/sidebar";
import ShowUserImage from "../files/ShowUserImage.vue";
import { countIncompleteDocumentations } from "../../utils/clientDocumentationStatus";
import ClientDocumentationForm from "./ClientDocumentationForm.vue";
import DocumentationDocRow from "./DocumentationDocRow.vue";

export default {
  name: "DocumentationPersonPanel",
  components: {
    ClientDocumentationForm,
    DocumentationDocRow,
    ProgressBar,
    ShowUserImage,
    Sidebar,
  },
  props: {
    clientId: { type: [String, Number], required: true },
    name: { type: String, default: "" },
    imgUrl: { type: String, default: null },
    relationLabel: { type: String, default: null },
    processIds: { type: Array, default: () => [] },
    isRelation: { type: Boolean, default: false },
    showInClientRelations: { type: Boolean, default: false },
    defaultExpanded: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      clientDocumentations: [],
      selectedDocumentation: null,
      sidebarVisible: false,
      expanded: this.defaultExpanded,
      isLoading: false,
      isSubmitting: false,
    };
  },
  computed: {
    incompleteCount() {
      return countIncompleteDocumentations(this.clientDocumentations);
    },
    initials() {
      const parts = this.name.trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    },
    avatarColor() {
      const palette = [
        "#3b82f6",
        "#8b5cf6",
        "#10b981",
        "#f59e0b",
        "#ef4444",
        "#06b6d4",
      ];
      let hash = 0;
      for (const character of String(this.clientId || this.name || "")) {
        hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
      }
      return palette[hash % palette.length];
    },
    formUploaderConfig() {
      return {
        uploadType: `client_data/${this.clientId}`,
        documentationUploadUrl: this.$root.api.fileManagerSave,
        inputUploadUrl: this.$root.api.fileManagerAskDocumentationsSave,
      };
    },
    formUiConfig() {
      return {
        inputsWrapperClass: "p-fluid formgrid grid doc-person__grid",
        inputColumnClass: "field col-12 md:col-6",
      };
    },
  },
  watch: {
    clientId() {
      this.load();
    },
    processIds: {
      handler() {
        this.load();
      },
      deep: true,
    },
    isRelation() {
      this.load();
    },
    showInClientRelations() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      if (!this.clientId) return;

      const params = {
        dataType: "list",
        clientId: this.clientId,
      };
      if (this.processIds && this.processIds.length) {
        params.processIds = this.processIds;
      }
      if (this.isRelation) {
        params.isRelation = true;
      }
      if (this.showInClientRelations) {
        params.showInClientRelations = true;
      }

      this.isLoading = true;
      this.$root
        .apiGet(this.$root.api.clientDocumentations, { params })
        .then((response) => {
          this.clientDocumentations = Array.isArray(response.data.data)
            ? response.data.data
            : [];
        })
        .catch((error) => {
          this.$root.toastError(
            (error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "an_error_has_occurred"
          );
          this.clientDocumentations = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    parseDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;

      const dateAsString = String(value).trim();
      const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateAsString);
      if (ddmmyyyy) {
        return new Date(
          Number(ddmmyyyy[3]),
          Number(ddmmyyyy[2]) - 1,
          Number(ddmmyyyy[1])
        );
      }

      const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateAsString);
      if (yyyymmdd) {
        return new Date(
          Number(yyyymmdd[1]),
          Number(yyyymmdd[2]) - 1,
          Number(yyyymmdd[3])
        );
      }
      return new Date(value);
    },
    normalizeInputValue(input) {
      if (input.type === "date" && input.value) {
        return this.parseDate(input.value);
      }
      if (input.type === "number") {
        if (this.isEmpty(input.value)) return null;
        const parsedNumber = Number(input.value);
        return Number.isNaN(parsedNumber) ? null : parsedNumber;
      }
      if (input.type === "checkbox") {
        return Boolean(input.value);
      }
      return input.value;
    },
    normalizeDocumentation(documentation, index) {
      const documentationType =
        documentation.documentationType || documentation;
      return {
        ...documentationType,
        uiId: documentation.id || `${documentationType.id}-${index}`,
        requestId: documentation.id,
        documentationTypeId: documentationType.id,
        expirationDate: this.parseDate(
          documentation.expirationDate || documentationType.expirationDate
        ),
        files: [
          ...(Array.isArray(documentation.files)
            ? documentation.files
            : documentationType.files || []),
        ],
        filesUploaded: [],
        inputs: Array.isArray(documentationType.inputs)
          ? documentationType.inputs.map((input) => ({
              ...input,
              value: this.normalizeInputValue(input),
              filesUploaded: [],
              filesPersisted: Array.isArray(input.filesPersisted)
                ? [...input.filesPersisted]
                : Array.isArray(input.files)
                ? [...input.files]
                : [],
            }))
          : [],
        clientId: this.clientId,
        clientName: this.name,
        relationClientId: this.clientId,
        isRelation: this.isRelation,
      };
    },
    openForm(documentation) {
      const index = this.clientDocumentations.indexOf(documentation);
      this.selectedDocumentation = this.normalizeDocumentation(
        documentation,
        index
      );
      this.sidebarVisible = true;
    },
    closeForm() {
      this.sidebarVisible = false;
      this.selectedDocumentation = null;
    },
    setDocumentationFiles(filesUploaded) {
      if (this.selectedDocumentation) {
        this.selectedDocumentation.filesUploaded = [...filesUploaded];
      }
    },
    setInputFiles(payload) {
      if (
        !this.selectedDocumentation ||
        !Array.isArray(this.selectedDocumentation.inputs)
      ) {
        return;
      }
      const input = this.selectedDocumentation.inputs[payload.inputIndex];
      if (input) {
        input.filesUploaded = [...payload.files];
      }
    },
    removeDocumentationFile(url) {
      if (!this.selectedDocumentation || !url) return;

      this.selectedDocumentation.filesUploaded =
        this.selectedDocumentation.filesUploaded.filter(
          (file) => file && file.url !== url
        );
      this.selectedDocumentation.files =
        this.selectedDocumentation.files.filter(
          (file) => file && file.url !== url
        );
    },
    removeInputFile(payload) {
      if (
        !this.selectedDocumentation ||
        !Array.isArray(this.selectedDocumentation.inputs) ||
        !payload ||
        !payload.url
      ) {
        return;
      }

      const input = this.selectedDocumentation.inputs[payload.inputIndex];
      if (!input) return;

      input.filesUploaded = (input.filesUploaded || []).filter(
        (file) => file && file.url !== payload.url
      );
      input.filesPersisted = (input.filesPersisted || []).filter(
        (file) => file && file.url !== payload.url
      );
    },
    handleFormUpload(event) {
      if (!event || !event.kind) return;

      if (event.kind === "documentation-files-uploaded") {
        this.setDocumentationFiles(event.payload);
      } else if (event.kind === "documentation-file-removed") {
        this.removeDocumentationFile(event.payload);
      } else if (event.kind === "input-files-uploaded") {
        this.setInputFiles(event.payload);
      } else if (event.kind === "input-file-removed") {
        this.removeInputFile(event.payload);
      } else if (event.kind === "upload-error") {
        this.isSubmitting = false;
      }
    },
    persistSelectedFiles() {
      if (!this.selectedDocumentation) return Promise.resolve();

      const files = [];
      const addFiles = (filesUploaded, suffix) => {
        (filesUploaded || []).forEach((fileUploaded) => {
          files.push({
            ...fileUploaded,
            context: `client_data/${this.clientId}/${suffix}`,
          });
        });
      };

      addFiles(this.selectedDocumentation.filesUploaded, "documentation-types");
      (this.selectedDocumentation.inputs || []).forEach((input) => {
        addFiles(input.filesUploaded, "documentation-inputs");
      });

      if (!files.length) return Promise.resolve();

      return this.$root
        .apiPost(this.$root.api.fileManagerAskDocumentationsPersist, {
          clientId: this.clientId,
          files,
        })
        .then((response) => {
          this.mergePersistedFiles(response.data.data || []);
        });
    },
    mergePersistedFiles(filesPersisted) {
      if (!this.selectedDocumentation) return;

      const appendPersisted = (target, persistedKey) => {
        if (!target.filesUploaded || !target.filesUploaded.length) return;

        target.filesUploaded.forEach((fileUploaded) => {
          const fileName = fileUploaded.url.substring(
            fileUploaded.url.lastIndexOf("/") + 1
          );
          const persistedFile = filesPersisted.find((file) =>
            file.url.endsWith(fileName)
          );
          if (persistedFile) {
            target[persistedKey] = [
              ...(target[persistedKey] || []),
              persistedFile,
            ];
          }
        });
        target.filesUploaded = [];
      };

      appendPersisted(this.selectedDocumentation, "files");
      (this.selectedDocumentation.inputs || []).forEach((input) => {
        appendPersisted(input, "filesPersisted");
      });
    },
    serializeSelectedEntry() {
      if (!this.selectedDocumentation) return null;

      const entry = this.selectedDocumentation;
      return {
        id: entry.requestId,
        files: Array.isArray(entry.files) ? [...entry.files] : [],
        expirationDate: this.$root.setDateFromCalendar(entry.expirationDate),
        documentationType: {
          id: entry.documentationTypeId,
          name: entry.name,
          description: entry.description,
          needsFileUpload: entry.needsFileUpload,
          isFilled: entry.isFilled,
          inputs: (entry.inputs || []).map((input) => ({
            ...input,
            value:
              input.type === "date"
                ? this.$root.setDateFromCalendar(input.value)
                : input.value,
            filesUploaded: undefined,
          })),
        },
      };
    },
    submitSelected() {
      if (!this.selectedDocumentation) return;

      this.isSubmitting = true;
      this.persistSelectedFiles()
        .then(() =>
          this.$root.apiPut(this.$root.api.clientDocumentations, {
            ...this.serializeSelectedEntry(),
            clientId: this.clientId,
          })
        )
        .then(() => {
          this.closeForm();
          this.load();
        })
        .catch((error) => {
          this.$root.toastError(
            (error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "an_error_has_occurred"
          );
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    isEmpty(value) {
      return value === null || value === undefined || value === "";
    },
  },
};
</script>

<style scoped lang="scss">
.doc-person {
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  background: #fff;
}

.doc-person__header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border: 0;
  background: #fff;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8fafc;
  }
}

.doc-person__identity,
.doc-person__meta {
  display: flex;
  align-items: center;
}

.doc-person__identity {
  flex: 1;
  gap: 0.45rem;
}

.doc-person__avatar {
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 999px;

  &--fallback {
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
  }
}

.doc-person__relation,
.doc-person__badge {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
}

.doc-person__relation {
  flex-shrink: 0;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.65rem;
}

.doc-person__name {
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 650;
  line-height: 1.25;
}

.doc-person__meta {
  flex-shrink: 0;
  gap: 0.4rem;
}

.doc-person__badge {
  padding: 0.12rem 0.4rem;
  border-radius: 0.3rem;
  font-size: 0.68rem;
  white-space: nowrap;

  &--pending {
    background: #ffedd5;
    color: #c2410c;
  }

  &--ok {
    background: #dcfce7;
    color: #166534;
  }
}

.doc-person__chevron {
  color: #64748b;
  font-size: 0.75rem;
}

.doc-person__progress {
  width: 100%;
  height: 0.2rem;
}

.doc-person__body {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.doc-person__sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 0.35rem;
}

.doc-person__sidebar-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.85rem;
  border-top: 1px solid #eceff5;
  background: #fff;
}

:deep(.doc-person__sidebar .p-sidebar) {
  background: #f7f9fc;
}

:deep(.doc-person__sidebar .p-sidebar-header) {
  padding: 1rem 1.25rem;
  background: #fff;
}

:deep(.doc-person__sidebar .p-sidebar-content) {
  padding: 1rem 1.25rem 1.25rem;
}

:deep(.doc-person__form .documentation-type-form__title) {
  margin: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

@media (max-width: 768px) {
  .doc-person__sidebar-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .doc-person--compact .doc-person__badge {
    display: none;
  }
}
</style>
